import React from 'react';

import * as messages from '@radnuc/messages';
import * as d3 from 'd3';
import {Config} from './Config';
import { t } from 'i18next';
import './i18n';
import {checkSnoozeState} from './SnoozeIsotopeDisplay'
import strong_lines_list from './spectrum_strong_lines_RFG';

import {positionType} from './SpectrumDisplay'
import './SpectrumDisplay.css'

type strong_lines = {name: string, name_hyphen: string, lines: number[]}
//type GammaSpectrum = { time: number, sum: number, spectrum: number[], livetime: number }
type DrifttimeCountPair = { drift_time: number, count_pos: number, count_neg: number }
//type NeutronData = { time: number, count: number, livetime: number }
type ChemResponseData = { time: number, value: number }
type ImsSpectrum = { time: number, pos: number[], neg: number[] }

export interface ChemDisplayProperty {
    gammaInLanePosition: number | undefined,
    neutronInLanePosition: number | undefined,
}
export interface ChemDisplayInterface {
    inLanePositionInit: ChemDisplayProperty,
    updateInLanePosition: (position: ChemDisplayProperty) => void,
}

export interface ChemDisplayProps {
    chemIdResults: messages.Chem.ChemIdentificationResult[] | null,
    raidReadings?: messages.Chem.RaidReading[] | null,
    //data2: messages.Rad.NeutronReading[] | null,
    //pcsResults: messages.Rad.PCSResult[] | null,
    liveStream: boolean,
    replayTime?: Date,
    isPrimary?: boolean,
    lanePosition?: number,
    multiLane?: boolean,
    showByPositionType?: positionType,
    spectrumDisplayInterface? : ChemDisplayInterface,
    config?: Config,
}

export interface ChemDisplayState {
    plotTimeRange: [number, number] | undefined
    liveSpectrumSumStartTime: number
}

export class ChemDisplay extends React.Component<ChemDisplayProps, ChemDisplayState> {
    //spectra: GammaSpectrum[];
    sourceSpectrum: DrifttimeCountPair[];
    chemResponsePositive: ChemResponseData[];
    chemResponseNegative: ChemResponseData[];
    imsSpectra: ImsSpectrum[];
    timestamps: number[];
    //counts: number[][];
    //neutronCounts: NeutronData[];
    min_channel_rate: number = 1e1;
    drift_time: number[];
    //plotTimeRange: [number,number] | undefined;
    lastTimeForceRedraw: number = 0;
    dominantIsotope: string | null = null;
    //gamma_max_in_lane_pos: number | undefined;
    gamma_possible_in_lane_pos: number[] = [];
    //neutron_max_in_lane_pos: number | undefined;
    neutron_possible_in_lane_pos: number[] = [];
    gammaInLanePosition?: number;
    neutronInLanePosition?: number;
    possible_lane_pos: number[] = [];
    lanePosition?: number;

    constructor(props: ChemDisplayProps) {
        super(props)

        this.state = {
            plotTimeRange: undefined,
            liveSpectrumSumStartTime: 0
        }
        this.chemResponsePositive = [];
        this.chemResponseNegative = [];
        this.imsSpectra = [];
        //this.counts = [];
        //this.neutronCounts = [];
        this.timestamps = []
        this.drift_time = [];
        for (var i = 0; i < 1024; ++i) {
            this.drift_time.push(0.0303*i);
        }
        this.sourceSpectrum = [];

        this.setSpectrumSumRange = this.setSpectrumSumRange.bind(this);
        this.setLiveSpectrumSumStartTime = this.setLiveSpectrumSumStartTime.bind(this);

        //this.calcGammaSpectrumAndCount = this.calcGammaSpectrumAndCount.bind(this);
    }

    componentDidUpdate(prevProps: ChemDisplayProps) {
        //if (this.props.radiationData !== prevProps.radiationData) {
            //this.calcGammaSpectrumAndCount();
            this.calcImsSpectrum();
            this.calcChemResponse();
        //}
        if(this.props.spectrumDisplayInterface !== undefined){
            this.gammaInLanePosition = this.props.spectrumDisplayInterface.inLanePositionInit.gammaInLanePosition;
            this.neutronInLanePosition = this.props.spectrumDisplayInterface.inLanePositionInit.neutronInLanePosition;
        }
    }

    addVector(a: number[], b: number[] | null) {
        if (b !== null)
            return a.map((e, i) => e + b[i]);
        else
            return a;
    }

    get_latest_spectrum(){
        let ims_spectra_descending = this.imsSpectra.sort(function (x, y) { return d3.descending(x.time, y.time); });
        let sum_pos_spectrum = new Array(1024).fill(0.0);
        let sum_neg_spectrum = new Array(1024).fill(0.0);
        let num_pos_spec = 0;
        let num_neg_spec = 0;
        let i_pos=0;
        let do_search_flag = true;
        //find the first nonzero pos spectrum
        while (num_pos_spec===0 && do_search_flag){
            if(Math.max.apply(null, ims_spectra_descending[i_pos].pos)>0) {
                sum_pos_spectrum = ims_spectra_descending[i_pos].pos;
                num_pos_spec = 1;
            } else {
                i_pos = i_pos + 1;
            }
            if(i_pos >= 10){
                do_search_flag = false
            }
        }
        let i_neg=0;
        do_search_flag = true;
        //find the first nonzero neg spectrum
        while (num_neg_spec===0){
            if(Math.max.apply(null, ims_spectra_descending[i_neg].neg)>0) {
                sum_neg_spectrum = ims_spectra_descending[i_neg].neg;
                num_neg_spec = 1;
            } else {
                i_neg = i_neg +1;
            }
            if(i_neg >= 10){
                do_search_flag = false
            }
        }

        let dt_sum_spectrum: DrifttimeCountPair[] = [];
        this.min_channel_rate = 1 / 1024.0;

        this.drift_time.forEach((dt, idx) => {
            //This sets the floor of the spectrum plot with livetime accounted
            let pos_count = sum_pos_spectrum[idx];
            if (pos_count < this.min_channel_rate * num_pos_spec)
                pos_count = this.min_channel_rate * num_pos_spec;

            let neg_count = sum_neg_spectrum[idx];
            if (neg_count < this.min_channel_rate * num_neg_spec)
                neg_count = this.min_channel_rate * num_neg_spec;

            dt_sum_spectrum.push(
                { drift_time: this.drift_time[idx], count_pos: pos_count/num_pos_spec, count_neg: neg_count/num_neg_spec});
        }, this);

        return dt_sum_spectrum;
    }

    sum_spectra(start_time: number, end_time: number) {
        var spectra = this.imsSpectra.filter(function (d: any) {
            return d.time > start_time && d.time < end_time;
        });

        let sum_pos_spectrum = new Array(1024).fill(0.0);
        let sum_neg_spectrum = new Array(1024).fill(0.0);
        let num_pos_spec = 0;
        let num_neg_spec = 0;
        spectra.forEach(function (spec) {
            if (spec.pos !== null && spec.pos !== undefined) {
                spec.pos.forEach(function (cnt, idx) {
                    sum_pos_spectrum[idx] += cnt;
                });
                num_pos_spec+=1;
            }
            if (spec.neg !== null && spec.neg !== undefined) {
                spec.neg.forEach(function (cnt, idx) {
                    sum_neg_spectrum[idx] += cnt;
                });
                num_neg_spec+=1;
            }
        });

        let dt_sum_spectrum: DrifttimeCountPair[] = [];
        this.min_channel_rate = 1 / spectra.length;

        this.drift_time.forEach((dt, idx) => {
            //This sets the floor of the spectrum plot with livetime accounted
            let pos_count = sum_pos_spectrum[idx];
            if (pos_count < this.min_channel_rate * num_pos_spec)
                pos_count = this.min_channel_rate * num_pos_spec;

            let neg_count = sum_neg_spectrum[idx];
            if (neg_count < this.min_channel_rate * num_neg_spec)
                neg_count = this.min_channel_rate * num_neg_spec;

            dt_sum_spectrum.push(
                { drift_time: this.drift_time[idx], count_pos: pos_count/num_pos_spec, count_neg: neg_count/num_neg_spec});
        }, this);

        return dt_sum_spectrum;
    }

    setSpectrumSumRange(start_time: number | null, end_time: number | null) {
        const timenow = Date.now();
        if (timenow > this.lastTimeForceRedraw + 50) {
            if (start_time !== null && end_time !== null) {
                if (start_time <= end_time) {
                    this.setState(() => ({
                        plotTimeRange: [start_time, end_time]
                    }));
                    //console.log("start time: " + start_time.valueOf() + " end time " + end_time.valueOf());
                } else {
                    this.setState(() => ({
                        plotTimeRange: undefined
                    }));
                }
            } else {
                this.setState(() => ({
                    plotTimeRange: undefined
                }));
            }
            this.lastTimeForceRedraw = timenow;
        }
    }

    setLiveSpectrumSumStartTime(start_time: number){
        const timenow = Date.now();
        if (timenow > this.lastTimeForceRedraw + 50) {
            this.setState(() => ({
                liveSpectrumSumStartTime: start_time
            }));
            this.lastTimeForceRedraw = timenow;
        }
    }

    calcImsSpectrum() {
        //Calculates count sum and average spectrum of all detectors
        this.imsSpectra = [];

        var livetime = 0.0;
        var sum_spectrum: number | null = 0.0;

        this.gamma_possible_in_lane_pos = [];
        this.possible_lane_pos = [];
        if (this.props.raidReadings !== undefined && this.props.raidReadings !== null && this.props.raidReadings.length > 0) {
            this.props.raidReadings.forEach((data) => {
                //if (data.time > time_now - 120000) {
                var this_sum_spectrum = 0.0;
                livetime = 0;
                let pos_spectrum: number[] | null = new Array(1024).fill(0.0);
                let neg_spectrum: number[] | null = new Array(1024).fill(0.0);
                //Average of all (Default)
                if(data.reading !== null){
                    pos_spectrum = this.addVector(data.reading.positiveSpectrum, pos_spectrum);
                    neg_spectrum = this.addVector(data.reading.negativeSpectrum, neg_spectrum);
                }

                if (data.time !== null) {
                    var result = {
                        time: data.time,
                        pos: pos_spectrum,
                        neg: neg_spectrum,
                    };
                    this.imsSpectra.push(result);
                }
            });
        }
        this.imsSpectra.sort(function (x, y) { return d3.ascending(x.time, y.time); });

        if (this.imsSpectra.length > 3) {
            if (this.state.plotTimeRange === undefined) {
                //No time range selected
                if(this.props.liveStream === false){
                    //If viewing replay data, don't show dominant if there's no timerange and also clear dominant isotope
                    this.dominantIsotope = null;
                    this.sourceSpectrum = this.sum_spectra(this.imsSpectra[0].time, this.imsSpectra[this.imsSpectra.length - 1].time);
                } else {
                    //If viewing live data, show latest spectra
                    this.dominantIsotope = null;
                    this.sourceSpectrum = this.get_latest_spectrum();
                }
            } else {
                //time range selected
                if(this.state.plotTimeRange[1] > this.state.liveSpectrumSumStartTime) {
                    //If there is a plot timerange, show dominant in the timerange
                    const start_time = Math.max(this.state.liveSpectrumSumStartTime,this.state.plotTimeRange[0]);
                    this.sourceSpectrum = this.sum_spectra(start_time, this.state.plotTimeRange[1]);
                } else {
                    //Time range is invalid now (scrolled off)
                    this.setSpectrumSumRange(null, null);
                    if(this.props.liveStream === false){
                        //Replay
                        this.sourceSpectrum = this.sum_spectra(this.imsSpectra[0].time, this.imsSpectra[this.imsSpectra.length - 1].time);
                    } else {
                        //If viewing live data, show latest spectra
                        this.sourceSpectrum = this.get_latest_spectrum();
                    }
                }
            }
        } else {
            this.sourceSpectrum = new Array(1024).fill(0.0);
            this.dominantIsotope = null;
        }
    }

    calcChemResponse(){
        this.chemResponsePositive = [];
        this.chemResponseNegative = [];
        const anomaly_agent = 'Anomaly';
        this.neutron_possible_in_lane_pos = [];
        if (this.props.chemIdResults !== null && this.props.chemIdResults.length > 0) {
            this.props.chemIdResults.forEach((data) => {

                var anomaly_result = 0.0;
                if(data.algorithm_results !== null && data.algorithm_results.length >= 0){
                    data.algorithm_results.forEach(result => {
                        if(result.agent === anomaly_agent)
                            anomaly_result = result.concentration;
                    });
                } else {
                    console.log('skipped!')
                }
                const result = {
                    time: data.time,
                    value: anomaly_result
                };
                if(data.polarity === messages.Chem.Polarity.POSITIVE)
                    this.chemResponsePositive.push(result);
                else if(data.polarity === messages.Chem.Polarity.NEGATIVE)
                    this.chemResponseNegative.push(result);
                //else {
                //    console.log('not positive or negative????')
                //}
            });
        }

    };

    handleGammaDetSelection(selection?:number){
            this.gammaInLanePosition = selection;
    }
    handleNeutronDetSelection(selection?:number){
            this.neutronInLanePosition = selection;
    }

    makeDetectorSelectDisplay(){
        let detectorSelectDisplay;
        let gammaButtons:JSX.Element[] = [];
        //if(this.gamma_max_in_lane_pos!==undefined){
        //    for(var i=0; i<=this.gamma_max_in_lane_pos;i++){
        if(this.gamma_possible_in_lane_pos.length > 0){
            this.gamma_possible_in_lane_pos.forEach((i)=>{
                const inLanePos = i;
                let thisSelectedClass;
                if(this.gammaInLanePosition === inLanePos )
                    thisSelectedClass = 'selected';
                const this_button =
                    <div key={inLanePos} className={"tiny-button "+thisSelectedClass}
                                onClick={() => this.handleGammaDetSelection(inLanePos)}>{inLanePos}</div>
                gammaButtons.push(this_button)
            });
            let allSelectedClass;
            if(this.gammaInLanePosition === undefined)
                allSelectedClass = 'selected';
            const all_button =
                <div key={100} className={"tiny-button "+allSelectedClass}
                            onClick={() => this.handleGammaDetSelection()}>All</div>
            gammaButtons.push(all_button)
        }

        let neutronButtons:JSX.Element[] = [];
        //if(this.neutron_max_in_lane_pos!==undefined){
        //    for(var i=0; i<=this.neutron_max_in_lane_pos;i++){
        if(this.neutron_possible_in_lane_pos.length > 0){
            this.neutron_possible_in_lane_pos.forEach((i)=>{
                const inLanePos = i;
                let thisSelectedClass;
                if(this.neutronInLanePosition === inLanePos )
                    thisSelectedClass = 'nselected';
                const this_button =
                    <div key={inLanePos} className={"tiny-button "+thisSelectedClass}
                                onClick={() => this.handleNeutronDetSelection(inLanePos)}>{inLanePos}</div>
                neutronButtons.push(this_button)
            });
            let allSelectedClass;
            if(this.neutronInLanePosition === undefined)
                allSelectedClass = 'nselected';
            const all_button =
                <div key={100} className={"tiny-button "+allSelectedClass}
                            onClick={() => this.handleNeutronDetSelection()}>All</div>
            neutronButtons.push(all_button)
        }
        detectorSelectDisplay =
            <div id='detector-select-container' style={{ display: 'flex', flexDirection: 'row', justifyContent:'flex-end', marginRight:'5px'}}>
                <span style={{color:'olivedrab',marginRight:'10px'}}>Gamma Detector Position</span>{gammaButtons}
                <div style={{marginRight:'20px'}}/>
                <span style={{color:'steelblue',marginRight:'10px'}}>Neutron Detector Position</span>{neutronButtons}
            </div>
        return(
            <div style={{position: 'relative'}}>
            {detectorSelectDisplay}
            </div>
        )

    }
    handleLaneSelection(selection?:number){
            this.lanePosition = selection;
    }
    makeLaneSelectDisplay(){
        let laneSelectDisplay;
        let laneButtons:JSX.Element[] = [];
        if(this.possible_lane_pos.length > 0){
            this.possible_lane_pos.forEach((i)=>{
                const lanePos = i;
                let thisSelectedClass;
                if(this.lanePosition === lanePos )
                    thisSelectedClass = 'selected';
                const this_button =
                    <div key={lanePos} className={"tiny-button "+thisSelectedClass}
                                onClick={() => this.handleLaneSelection(lanePos)}>{lanePos}</div>
                laneButtons.push(this_button)
            });
            let allSelectedClass;
            if(this.lanePosition === undefined)
                allSelectedClass = 'selected';
            const all_button =
                <div key={100} className={"tiny-button "+allSelectedClass}
                            onClick={() => this.handleLaneSelection()}>All</div>
            laneButtons.push(all_button)
        }

        laneSelectDisplay =
            <div id='detector-select-container' style={{ display: 'flex', flexDirection: 'row', justifyContent:'flex-end', marginRight:'5px'}}>
                <span style={{color:'white',marginRight:'10px'}}>Position</span>{laneButtons}
            </div>
        return(
            <div style={{position: 'relative'}}>
            {laneSelectDisplay}
            </div>
        )

    }

    handlePositionSelection(selection?:number){
            this.gammaInLanePosition = selection;
            this.neutronInLanePosition = selection;
            if(this.props.spectrumDisplayInterface){
                const spectrumDisplayPositions:ChemDisplayProperty = {gammaInLanePosition:this.gammaInLanePosition,
                                                                          neutronInLanePosition:this.neutronInLanePosition}
                this.props.spectrumDisplayInterface.updateInLanePosition(spectrumDisplayPositions);
            }

            //if(this.neutron_possible_in_lane_pos.length > 1){
            //    this.neutronInLanePosition = selection;
            //} else {
            //    this.neutronInLanePosition = undefined;
            //}

    }
    makePositionSelectDisplay(){
        let positionSelectDisplay;
        let positionButtons:JSX.Element[] = [];
        let positionNameLUT:{[position:number]:string}|undefined;
        if(this.props.showByPositionType === positionType.BEARING){
            positionNameLUT = {0:'L',1:'R'}
        } else if(this.props.showByPositionType === positionType.IN_LANE){
            positionNameLUT = {0:'B',1:'T'}
        } else {
            positionNameLUT = undefined;
        }

        const combined_possible_in_lane_pos = Array.prototype.concat(this.gamma_possible_in_lane_pos,this.neutron_possible_in_lane_pos).filter((v, i, a) => a.indexOf(v) === i);;
        if(combined_possible_in_lane_pos.length > 0 ){
            combined_possible_in_lane_pos.sort((a,b) => a-b).forEach((i)=>{
                const lanePos = i;
                let lanePosString;
                if(lanePos !== null){
                    lanePosString = lanePos.toFixed(0);
                }
                if(positionNameLUT !== undefined){
                    lanePosString = t(positionNameLUT[lanePos],{ns:'trans'});
                }
                let thisSelectedClass;
                if(this.gammaInLanePosition === lanePos )
                    thisSelectedClass = 'selected';
                const this_button =
                    <div key={lanePos} className={"tiny-button "+thisSelectedClass}
                                onClick={() => this.handlePositionSelection(lanePos)}>{lanePosString}</div>
                if(lanePos !== null)
                    positionButtons.push(this_button)
            });
            let allSelectedClass;
            if(this.gammaInLanePosition === undefined)
                allSelectedClass = 'selected';
            const all_button =
                <div key={100} className={"tiny-button "+allSelectedClass}
                            onClick={() => this.handlePositionSelection()}>{t('All',{ns:'trans'})}</div>
            positionButtons.push(all_button)
        }

        positionSelectDisplay =
            <div id='detector-select-container' style={{ display: 'flex', flexDirection: 'row', justifyContent:'flex-end', marginRight:'5px'}}>
                <span style={{color:'white',marginRight:'10px'}}>{t('Position',{ns:'trans'})}</span>{positionButtons}
            </div>
        return(
            <div style={{position: 'relative'}}>
            {positionSelectDisplay}
            </div>
        )

    }

    render() {
        let detectorSelectDisplay; // =  this.makeDetectorSelectDisplay();
        if(this.props.isPrimary !== undefined){
            if(this.props.isPrimary){
                //Primary
                if(this.props.lanePosition !== undefined){
                    detectorSelectDisplay =  this.makeDetectorSelectDisplay();
                }
            } else {
                //Secondary
                detectorSelectDisplay =  this.makeDetectorSelectDisplay();
            }
        }
        let laneSelectDisplay;
        if(this.props.multiLane){
            laneSelectDisplay = this.makeLaneSelectDisplay();
        }
        let positionSelectDisplay;
        if(this.props.showByPositionType){
            positionSelectDisplay = this.makePositionSelectDisplay();
        }

        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {laneSelectDisplay}
                {detectorSelectDisplay}
                {positionSelectDisplay}
                <div style={{ display: 'flex', flexDirection: 'column', height: '50%' }}>
                <MemoizedChemResponseHistoryPlot data={this.chemResponsePositive} data2={this.chemResponseNegative} replayTime={this.props.replayTime} liveStream={this.props.liveStream}
                    onSelectRange={this.setSpectrumSumRange}  setStartTime={this.setLiveSpectrumSumStartTime}/>
                </div>
            </div>
        );
        //<MemoizedImsSpectrumPlot data={this.sourceSpectrum} title={'IMS Spectrum'} dominantIsotope={this.dominantIsotope}/>
    }

}
export default ChemDisplay

//******************************************************************************
export interface ChemResponseHistoryPlotProps {
    data: ChemResponseData[],
    data2?: ChemResponseData[],
    replayTime?: Date,
    liveStream: boolean,
    onSelectRange?: (start_time: number | null, end_time: number | null) => void;
    setStartTime?: (start_time: number) => void;
}

export interface ChemResponseHistoryPlotState {
    counthistory_log_scale: boolean,
}

export class ChemResponseHistoryPlot extends React.Component<ChemResponseHistoryPlotProps,ChemResponseHistoryPlotState> {
    base: HTMLDivElement | null
    updateRef: (element: HTMLDivElement) => void
    previous_brush: [number, number] | undefined;

    plot_margin = { top: 20, right: 55, bottom: 30, left: 55 };
    plot1_count_rate_x: d3.ScaleTime<number, number> | null = null;
    plot1_count_rate_y: d3.ScaleContinuousNumeric<number, number> | null = null;
    plot1_count_rate_g: any | null
    plot2_count_rate_x: d3.ScaleTime<number, number> | null = null;
    plot2_count_rate_y: d3.ScaleContinuousNumeric<number, number> | null = null;
    plot2_count_rate_g: any | null
    count_rate_width: number = 0
    count_rate_height: number = 0
    source_brush: d3.BrushBehavior<unknown> | null = null;
    brush_path: any;
    history_plot_range_msec = 120000;
    plot_scale_min_plot1 = 40; //concentration units
    plot_scale_min_plot2 = 40; //concentration units
    plot_scale_plus_min = 50;

    constructor(props: ChemResponseHistoryPlotProps) {
        super(props)
        this.base = null

        this.state = {
            counthistory_log_scale: false,
        }

        this.resize = this.resize.bind(this);
        this.set_counthistory_log_scale = this.set_counthistory_log_scale.bind(this);

        this.updateRef = (element: HTMLDivElement) => {
            this.base = element;
            this.renderPlot();
        };
    }

    componentDidMount() {
        this.initPlot()
        this.renderPlot();
        window.addEventListener("resize", this.resize);
    }

    componentDidUpdate(prevProps: ChemResponseHistoryPlotProps, prevState: ChemResponseHistoryPlotState) {
        if (this.props.data !== prevProps.data) {
            this.renderPlot();
        }
        if (this.state.counthistory_log_scale !== prevState.counthistory_log_scale) {
            //Switch plot scale
            this.renderPlot();
        }
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.resize);
    }

    resize() {
        this.initPlot()
        this.renderPlot();
    }

    set_counthistory_log_scale(flag: boolean) {
        this.setState(state => ({
            counthistory_log_scale: flag
        }));
    }

    on_source_brush() {
        if (this.plot1_count_rate_x !== null && this.plot1_count_rate_y !== null) {
            var sel = d3.event.selection || this.plot1_count_rate_x.range();
            var times = sel.map(this.plot1_count_rate_x.invert, this.plot1_count_rate_x);
            times[0] = Math.round(times[0] / 1000) * 1000;
            times[1] = Math.round(times[1] / 1000) * 1000;
            if (d3.event.selection !== null) {
                if (this.previous_brush === undefined) {
                    if(this.props.onSelectRange !== undefined){
                        this.props.onSelectRange(times[0], times[1])
                        this.previous_brush = times;
                    }
                } else {
                    if (this.previous_brush[0] !== times[0] || this.previous_brush[1] !== times[1]) {
                        if(this.props.onSelectRange !== undefined){
                            this.props.onSelectRange(times[0], times[1])
                            this.previous_brush = times;
                        }
                    }
                }
            } else {
                if (this.previous_brush !== undefined) {
                    if(this.props.onSelectRange !== undefined){
                        this.props.onSelectRange(null, null);
                        this.previous_brush = undefined;
                        if (this.source_brush !== null)
                            this.brush_path.call(this.source_brush.move, null);
                    }
                }
            }
        }
    }

    initPlot() {
        const width = this.base!.clientWidth - 20;  // subtract 20 to compensate for margins
        const height = this.base!.clientHeight - 20;

        this.count_rate_width = width -
            this.plot_margin.left - this.plot_margin.right;
        this.count_rate_height = height -
            this.plot_margin.top - this.plot_margin.bottom;

        d3.select(this.base!).select("svg").remove();

        const count_rate_svg = d3.select(this.base!).append("svg")
            .attr("width", `${width}px`)
            .attr("height", `${height}px`);

        //Neutron Plot
        this.plot2_count_rate_g = count_rate_svg.append('g').attr(
            'transform', 'translate(' + this.plot_margin.left + ',' +
            this.plot_margin.top + ')');

        this.plot2_count_rate_g.selectAll('.plot-axis').remove();
        this.plot2_count_rate_g.selectAll('path').remove();

        this.plot2_count_rate_x =
            d3.scaleTime().rangeRound([0, this.count_rate_width]);
        this.plot2_count_rate_y =
            d3.scaleLinear().rangeRound([this.count_rate_height,0]);

        //Gamma Plot
        this.plot1_count_rate_g = count_rate_svg.append('g').attr(
            'transform', 'translate(' + this.plot_margin.left + ',' +
            this.plot_margin.top + ')');

        this.plot1_count_rate_g.selectAll('.plot-axis').remove();
        this.plot1_count_rate_g.selectAll('path').remove();

        this.plot1_count_rate_x =
            d3.scaleTime().rangeRound([0, this.count_rate_width]);
        this.plot1_count_rate_y =
            d3.scaleLinear().rangeRound([this.count_rate_height, 0]);

        this.source_brush =
            d3.brushX().on('brush end', this.on_source_brush.bind(this));

        this.brush_path = this.plot1_count_rate_g.append('g')
            .attr('class', 'brush')
            .call(this.source_brush);
    }

    renderPlot() {
        if (this.plot1_count_rate_x !== null && this.plot1_count_rate_y !== null) {
            let max_all_x = 0;
            let min_plot_x = 0;

            //Plot Scale
            if (this.state.counthistory_log_scale) {
                this.plot1_count_rate_y = d3.scaleLog().rangeRound([this.count_rate_height, 0]);
                this.plot2_count_rate_y = d3.scaleLog().rangeRound([this.count_rate_height, 0]);
            } else {
                this.plot1_count_rate_y = d3.scaleLinear().rangeRound([this.count_rate_height, 0]);
                this.plot2_count_rate_y = d3.scaleLinear().rangeRound([this.count_rate_height, 0]);
            }

            //Find common x domain between gamma and neutron data
            if(this.props.data2 !== undefined && this.props.data2 !== null){
                //Neutron data exists
                if(this.plot2_count_rate_x !== null && this.plot2_count_rate_y !== null){
                    if(this.props.data.length > 0){
                        //Neutron and Gamma data exist
                        let max_gamma_x = d3.max(this.props.data, function (d) { return d.time }) || 0;
                        let min_gamma_x = d3.min(this.props.data, function (d) { return d.time }) || 0;

                        let max_neutron_x = d3.max(this.props.data2, function (d) { return d.time }) || 0;
                        let min_neutron_x = d3.min(this.props.data2, function (d) { return d.time }) || 0;

                        max_all_x = Math.max(max_gamma_x,max_neutron_x);
                        var min_all_x=0;
                        if(min_gamma_x > 0){
                            if(min_neutron_x > 0){
                                min_all_x = Math.min(min_gamma_x,min_neutron_x);
                            } else {
                                min_all_x = min_gamma_x;
                            }
                        } else {
                            if(min_neutron_x > 0){
                                min_all_x = min_neutron_x;
                            } else {
                                min_all_x = 0;
                            }
                        }

                        min_plot_x = min_all_x;

                        if(this.props.liveStream === true){
                            if(min_plot_x < max_all_x-this.history_plot_range_msec && min_plot_x > 0){
                                min_plot_x = max_all_x-this.history_plot_range_msec;
                            }
                        }

                        this.plot2_count_rate_x.domain([
                            min_plot_x,
                            max_all_x
                        ]);
                        this.plot1_count_rate_x = this.plot2_count_rate_x;

                    } else {
                        //Neutron data only
                        this.plot2_count_rate_x.domain([
                            d3.min(this.props.data2, function (d) { return d.time }) || 0,
                            d3.max(this.props.data2, function (d) { return d.time }) || 0
                        ]);
                        this.plot1_count_rate_x = this.plot2_count_rate_x;
                    }
                }

            } else {
                //Neutron data doesn't exist
                if(this.props.data.length > 0){
                    //Gamma data only
                    this.plot1_count_rate_x.domain([
                        d3.min(this.props.data, function (d) { return d.time }) || 0,
                        d3.max(this.props.data, function (d) { return d.time }) || 0
                    ]);
                    this.plot2_count_rate_x = this.plot1_count_rate_x;
                    const domain = this.plot1_count_rate_x.domain();
                    min_plot_x = domain[0].getTime();
                } else {
                    //NO VALID DATA

                }
            }
            if(this.props.setStartTime !== undefined)
                this.props.setStartTime(min_plot_x);

            //Y axis limits
            var plot1_maxval_y = 0;
            var plot1_minval_y = 100000;

            const validData = this.props.data.filter(function (d) { return d.value !== null }).filter(function (d) { return d.time >= min_plot_x});;
            this.plot1_count_rate_y.domain([
                d3.min(validData, function (d) { return d.value }) || 0,
                d3.max(validData, function (d) { return d.value }) || 0
            ]);

            //Find max and min of gamma
            if(this.plot1_count_rate_y.domain()[1] > plot1_maxval_y){
                plot1_maxval_y = this.plot1_count_rate_y.domain()[1];
            }
            if(this.plot1_count_rate_y.domain()[0] < plot1_minval_y){
                plot1_minval_y = this.plot1_count_rate_y.domain()[0];
            }

            const log_gamma_floor_val = 1e1;
            if (this.plot1_count_rate_y.domain()[1] < this.plot1_count_rate_y.domain()[0]+this.plot_scale_plus_min){
                let plot1_minval = this.plot1_count_rate_y.domain()[0];
                if(!this.state.counthistory_log_scale && plot1_minval < 1){
                    plot1_minval = 0; //If linear scale and min val is <1
                } else if (this.state.counthistory_log_scale) {
                    plot1_minval = log_gamma_floor_val;
                }
                this.plot1_count_rate_y.domain([plot1_minval,this.plot1_count_rate_y.domain()[0]+this.plot_scale_plus_min]);
            }

            var xscale = this.plot1_count_rate_x;
            var yscale = this.plot1_count_rate_y;

            // clang-format off
            this.plot1_count_rate_g.selectAll('.plot-axis').remove();
            this.plot1_count_rate_g.selectAll('.replay-time').remove();
            this.plot1_count_rate_g.selectAll('path').remove();
            this.plot2_count_rate_g.selectAll('.neutron-plot-axis').remove();
            this.plot2_count_rate_g.selectAll('path').remove();

            //Gamma Plot
            this.plot1_count_rate_g.append('g')
                .attr('class', 'plot-axis')
                .attr('transform', 'translate(0,' + this.count_rate_height + ')')
                .call(d3.axisBottom(xscale).scale(xscale))

            this.plot1_count_rate_g.append('g')
                .attr('class', 'plot-axis')
                .call(d3.axisLeft(yscale).ticks(5,d3.format(",d")));
                //.call(d3.axisLeft(yscale).ticks(4).tickFormat(x => `${x.valueOf().toFixed(0)}`));

            this.plot1_count_rate_g.append('g')
                .attr('class', 'plot-axis')
                .append("text")

                // this makes it easy to centre the text as the transform is applied to the anchor
                .attr("text-anchor", "middle")

                // text is drawn off the screen top left, move down and out and rotate
                .attr("transform", "translate(-" + this.plot_margin.left * 0.7 + "," +
                    (this.count_rate_height / 2) + ")rotate(-90)")

                .attr("fill", 'olivedrab')
                .text(t("Pos. Mode Arb. Chem. Resp.",{ns:'trans'}));

            this.plot1_count_rate_g.append('path')
                .datum(this.props.data)
                .attr('fill', 'none')
                .attr('stroke', 'olivedrab')
                .attr('stroke-width', 1.5)
                .attr('d', d3.line()
                    .x(function (d: any) { return xscale(d.time) as number})
                    .defined(function (d: any) { return d.value !== null; })
                    .defined(function (d: any) { return d.time > min_plot_x; })
                    .y(function (d: any) { return yscale(d.value) as number })
                );

            //Neutron plot
            if(this.props.data2 !== undefined && this.props.data2 !== null){
                let validData2 = this.props.data2.filter(function (d) { return d.value !== null}).filter(function (d) { return d.time >= min_plot_x})
                const log_neutron_floor_val = log_gamma_floor_val;
                if(this.state.counthistory_log_scale){
                    validData2.forEach(el => {
                        if(el.value === 0)
                            el.value = log_neutron_floor_val;
                    });
                }
                if(this.plot2_count_rate_x !== null && this.plot2_count_rate_y !== null){
                    var plot2_maxval_y = 0;
                    var plot2_minval_y = 100000;
                    this.plot2_count_rate_y.domain([
                        d3.min(validData2, function (d) { return d.value }) || 0,
                        d3.max(validData2, function (d) { return d.value }) || 0
                    ]);
                    //Find max and min of gamma
                    if(this.plot2_count_rate_y.domain()[1] > plot2_maxval_y){
                        plot2_maxval_y = this.plot2_count_rate_y.domain()[1];
                    }
                    if(this.plot2_count_rate_y.domain()[0] < plot2_minval_y){
                        plot2_minval_y = this.plot2_count_rate_y.domain()[0];
                    }
                    //Set range for plots
                    if (plot2_maxval_y < this.plot_scale_min_plot2){
                        this.plot2_count_rate_y.domain([plot2_minval_y,this.plot_scale_min_plot2]);
                    }
                    if (this.plot2_count_rate_y.domain()[1] < this.plot2_count_rate_y.domain()[0]+this.plot_scale_plus_min){
                        let plot2_minval = this.plot2_count_rate_y.domain()[0];
                        if(!this.state.counthistory_log_scale && plot2_minval < 1){
                            plot2_minval = 0; //If linear scale and min val is <1
                        } else if (this.state.counthistory_log_scale) {
                            plot2_minval = log_neutron_floor_val;
                        }
                        this.plot2_count_rate_y.domain([plot2_minval,this.plot2_count_rate_y.domain()[0]+this.plot_scale_plus_min]);
                    }

                    let neutron_xscale = this.plot2_count_rate_x;
                    let neutron_yscale = this.plot2_count_rate_y;

                    this.plot2_count_rate_g.append('g')
                        .attr('class', 'neutron-plot-axis')
                        .attr('transform', 'translate(0,' + this.count_rate_height + ')')
                        .call(d3.axisBottom(neutron_xscale).scale(neutron_xscale))


                    this.plot2_count_rate_g.append('g')
                        .attr('class', 'neutron-plot-axis')
                        .attr('transform', 'translate('+ this.count_rate_width + ',0)')
                        .call(d3.axisRight(neutron_yscale).ticks(5,d3.format(",d")));

                    this.plot2_count_rate_g.append('g')
                        .attr('class', 'neutron-plot-axis')
                        .append("text")
                        .attr("text-anchor", "middle")  // this makes it easy to centre the text as the transform is applied to the anchor
                        .attr("transform", "translate("+ (this.count_rate_width+this.plot_margin.right*0.70) +","+(this.count_rate_height/2)+")rotate(90)")  // text is drawn off the screen top left, move down and out and rotate
                        .attr("fill",'steelblue')
                        .text(t("Neg. Mode Arb. Chem. Resp.",{ns:'trans'}));

                    if(this.state.counthistory_log_scale){
                        //log scale so remove zero points
                        this.plot2_count_rate_g.append('path')
                            .datum(validData2)
                            .attr('fill', 'none')
                            .attr('stroke', 'steelblue')
                            .attr('stroke-width', 1.5)
                            .attr('d',d3.line()
                                .x(function (d: any) { return neutron_xscale(d.time) as number })
                                .y(function (d: any) { return neutron_yscale(d.value) as number })
                                .defined(function (d: any) { return d.value !== null; })
                                .defined(function (d: any) { return d.time > min_plot_x; })
                                .defined(function (d: any) { return d.value > log_neutron_floor_val; })
                        );
                    } else {
                        //Linear scale so plot zero points
                        this.plot2_count_rate_g.append('path')
                            .datum(validData2)
                            .attr('fill', 'none')
                            .attr('stroke', 'steelblue')
                            .attr('stroke-width', 1.5)
                            .attr('d',d3.line()
                                .x(function (d: any) { return neutron_xscale(d.time) as number })
                                .y(function (d: any) { return neutron_yscale(d.value) as number })
                                .defined(function (d: any) { return d.value !== null; })
                                .defined(function (d: any) { return d.time > min_plot_x; })
                            );
                    }
                }
            }

            if (this.source_brush !== null) {
                if (this.previous_brush !== undefined) {
                    let xs = this.previous_brush.map(xscale);
                    if(xs[0] !== undefined && xs[1] !== undefined){
                        if (xs[0] < 0)
                            xs[0] = 0;

                        if (xs[1] > 0) {
                            this.brush_path.call(this.source_brush.move, xs);
                        } else {
                            this.previous_brush = undefined;
                            this.brush_path.call(this.source_brush.move, null);
                        }
                    }
                }
            }

            if (this.props.replayTime && !this.props.liveStream) {
                var pos = this.plot1_count_rate_x(this.props.replayTime);
                if (pos === undefined)
                    pos = 0;
                if (pos < 0)
                    pos = 0;
                if (pos > this.count_rate_width)
                    pos = this.count_rate_width;

                this.plot1_count_rate_g.append("line")
                    .attr('class', 'replay-time')
                    .attr("x1", pos)
                    .attr("y1", 0)
                    .attr("x2", pos)
                    .attr("y2", this.count_rate_height)
                    .style("stroke-width", 2)
                    .style("stroke", "red")
                    .style("fill", "none");
            }
        }
    }

    render() {
        //console.log('Render ChemResponseHistoryPlot')
        let linearSelectClass;
        let logSelectClass;
        if (this.state.counthistory_log_scale) {
            linearSelectClass = "";
            logSelectClass = "active";
        } else {
            linearSelectClass = "active";
            logSelectClass = "";
        }
        return (
            <div className="Plot" style={{ display: 'flex', flex: '1', flexDirection: 'column' }}>
                <div className="header">
                    <span>{t('Chemical Response History',{ns:'trans'})}</span>
                    <div className="icon-bar" style={{ paddingLeft: '10px', paddingBottom: '2px' }}>
                        <div className={"axis-type " + linearSelectClass} onClick={() => this.set_counthistory_log_scale(false)}>{t('LINEAR',{ns:'trans'})}</div>
                        <div className={"axis-type " + logSelectClass} onClick={() => this.set_counthistory_log_scale(true)}>{t('LOG',{ns:'trans'})}</div>
                    </div>
                </div>
                <div className="plot" ref={this.updateRef} style={{ display: 'flex', flex: '1', touchAction:'none' }} />
            </div>
        )
    }
}
const MemoizedChemResponseHistoryPlot = React.memo(ChemResponseHistoryPlot, chemResponseHistoryPlotPropsAreEqual)
function chemResponseHistoryPlotPropsAreEqual(prevProps:ChemResponseHistoryPlotProps,nextProps:ChemResponseHistoryPlotProps){

    let flag = true;
    if(prevProps.liveStream !== nextProps.liveStream){
        flag = false;
    }
    if(prevProps.replayTime?.getTime() !== nextProps.replayTime?.getTime()){
        flag = false;
    }
    if(nextProps.data !== null && nextProps.data.length > 0){
        if(prevProps.data !== null && prevProps.data.length > 0){
            if(prevProps.data[prevProps.data.length-1].time !== nextProps.data[nextProps.data.length-1].time){
                flag = false;
            }
        } else {
            flag = false;
        }
    } else if (prevProps.data !== null && prevProps.data.length > 0){
        flag = false;
    } else if(nextProps.data2 !== undefined && nextProps.data2.length > 0){
        if(prevProps.data2 !== undefined && prevProps.data2.length > 0){
            if(prevProps.data2[prevProps.data2.length-1].time !== nextProps.data2[nextProps.data2.length-1].time){
                flag = false;
            }
        } else {
            flag = false;
        }
    }

    return (flag);
}
//******************************************************************************

export interface ImsSpectrumPlotProps { data: any[], title: string, dominantIsotope: string | null }
export interface ImsSpectrumPlotState {
    spectrum_log_scale: boolean,
    show_spectrum_cursor: boolean,
    spectrum_transform: any | undefined,
    focus_location_i: any | undefined,
    focus_location_coord_x: any | undefined,
    focus_location_x0: any | undefined
}

export class ImsSpectrumPlot extends React.Component<ImsSpectrumPlotProps, ImsSpectrumPlotState, {}> {
    base: HTMLDivElement | null
    updateRef: (element: HTMLDivElement) => void

    plot_margin = { top: 20, right: 55, bottom: 30, left: 55 };
    spectrum_x: d3.ScaleContinuousNumeric<number, number> | null = null;
    spectrum_y: d3.ScaleContinuousNumeric<number, number> | null = null;
    spectrum_g: any | null
    spectrum2_y: d3.ScaleContinuousNumeric<number, number> | null = null;
    spectrum2_g: any | null
    spectrum_width: number = 0
    spectrum_height: number = 0
    focus: any
    focus2: any
    drift_time: any[];
    min_channel_rate = 1e1;

    strong_lines_list: strong_lines[] | undefined;

    cursor_mousedown_flag: boolean;

    constructor(props: ImsSpectrumPlotProps) {
        super(props)
        this.base = null
        this.state = {
            spectrum_log_scale: false,
            show_spectrum_cursor: false,
            spectrum_transform: undefined,
            focus_location_i: undefined,
            focus_location_coord_x: undefined,
            focus_location_x0: undefined
        }

        this.drift_time = [];
        for (var i = 0; i < 1024; ++i) {
            this.drift_time.push(0.0303*i);
        }

        this.resize = this.resize.bind(this);

        this.updateRef = (element: HTMLDivElement) => {
            this.base = element;
            this.renderPlot();
        };
        this.set_spectrum_log_scale = this.set_spectrum_log_scale.bind(this);

        this.strong_lines_list = strong_lines_list;
        this.cursor_mousedown_flag = false;
        this.cursor_grab = this.cursor_grab.bind(this);
    }

    componentDidMount() {
        this.initPlot()
        this.renderPlot();

        this.setState(() => ({
            spectrum_transform: d3.zoomIdentity.translate(-122.417,8.1379).scale(1.8) //Default zoom
        }));
        window.addEventListener("resize", this.resize);
    }

    componentDidUpdate(prevProps: ImsSpectrumPlotProps, prevState: ImsSpectrumPlotState) {
        if (this.props.data !== prevProps.data) {
            this.renderPlot();
        }
        if (this.state.spectrum_log_scale !== prevState.spectrum_log_scale) {
            //Switch plot scale
            this.renderPlot();
        }
    }
    componentWillUnmount() {
        window.removeEventListener("resize", this.resize);
    }

    set_spectrum_log_scale(flag: boolean) {
        this.setState(state => ({
            spectrum_log_scale: flag
        }));
    }

    resize() {
        this.initPlot()
        this.renderPlot();
    }

    toggle_spectrum_cursor() {
        this.setState(state => ({
            show_spectrum_cursor: !state.show_spectrum_cursor
        }));
        this.renderPlot();
    }


    zoomed() {
        if (this.state.spectrum_transform !== undefined) {
            this.check_bounds_spectral_transform(d3.event.transform);
        } else {
            this.setState(() => ({
                spectrum_transform: d3.event.transform
            }));
        }
        this.renderPlot();
    }

    mouseclick() {
        //if(d3.event.defaultPrevented) return; //dragged
        if (this.spectrum_x != null) {
            var coord = d3.mouse(this.base!);

            var xscale = this.spectrum_x;
            if (this.state.spectrum_transform !== undefined) {
                xscale = this.state.spectrum_transform.rescaleX(this.spectrum_x);
            }
            const focus_location_coord_x = coord[0] - this.plot_margin.left;
            var x0 = xscale.invert(focus_location_coord_x);

            this.setState(() => ({
                focus_location_coord_x: focus_location_coord_x
            }));
            this.setState(() => ({
                show_spectrum_cursor: true
            }));
            this.setState(() => ({
                focus_location_x0: x0
            }));

            this.renderCursor();
        }
    };

    cursor_dragged() {
        //console.log(d3.event);
        if (this.spectrum_x !== null) {
            var dx = d3.event.dx;// XXX: ;
            var xscale = this.spectrum_x;
            if (this.state.spectrum_transform !== undefined) {
                xscale = this.state.spectrum_transform.rescaleX(this.spectrum_x);
            }

            var x0 = xscale.invert(this.state.focus_location_coord_x + dx);
            //console.log("cursor_drag x: "+this.focus_location_coord_x+" x0: "+x0);
            if (x0 >= 0 && x0 < 31) {
                this.setState(() => ({
                    focus_location_x0: x0
                }));
                this.setState((state) => ({
                    focus_location_coord_x: state.focus_location_coord_x + dx
                }));

                this.renderCursor()
            }
        }
    }

    cursor_grab(flag:boolean){
        this.cursor_mousedown_flag = flag;
        this.renderCursor();
    }

    calculate_cursor_location() {
        let d = this.state.focus_location_x0;
        //var d:EnergyCountPair= {energy:this.state.focus_location_x0, count:0};
        let bisectEnergy = d3.bisector(function (d: any) { return d.drift_time; }).left;
        let spectra = this.props.data;

        if (spectra != null) {
            let i = bisectEnergy(spectra, this.state.focus_location_x0, 1),
                d0 = spectra[i - 1],
                d1 = spectra[i];
            d = this.state.focus_location_x0 - d0.drift_time > d1.drift_time - this.state.focus_location_x0 ? d1 : d0;

            this.setState({ focus_location_i: i});
        } else {
            this.setState({ focus_location_i: undefined });
        }

        return d;
    }

    check_bounds_spectral_transform(input_transform: any) {
        //This function tests the input transform to see if the resulting scale will fall
        //within the 0 to 3000 keV plot range.
        var tx = Math.min(0, Math.max(input_transform.x, this.spectrum_width - this.spectrum_width * input_transform.k));
        var test_transform = this.state.spectrum_transform;
        var test_xscale = test_transform.rescaleX(this.spectrum_x);

        if (test_xscale.domain()[0] >= 0 && test_xscale.domain()[1] < 31) {
            //Accept this transform as the result is in-bounds
            //this.spectrum_transform = input_transform;
            this.setState(() => ({
                spectrum_transform: input_transform
            }));
        } else {
            //Result is out of bounds. Set to valid value
            //this.spectrum_transform.x = tx;
            var valid_transform = this.state.spectrum_transform;
            valid_transform.x = tx;
            this.setState(() => ({
                spectrum_transform: valid_transform
            }));
        }
    }

    find_strong_lines(dominant_isotope:string){
        var lines:number[] = [];
        var hyphenated_nuclide_name:string = "";
        if(this.strong_lines_list !== undefined){
            this.strong_lines_list.forEach((isotope)=>{
                if(isotope.name.match(dominant_isotope)){
                    lines = isotope.lines;
                    hyphenated_nuclide_name = isotope.name_hyphen;
                }
            });
        }
        return {hyphenated_nuclide_name, lines};
    }

    find_data_baseline(){
        let pos_baseline = 1000;
        let neg_baseline = 1000;
        let pos_median = d3.median(this.props.data,function(d){return d.count_pos});
        let neg_median = d3.median(this.props.data,function(d){return d.count_neg});
        if( pos_median === undefined)
            pos_median = 0
        if( neg_median === undefined)
            neg_median = 0

        this.props.data.forEach(el => {
            if(el.count_pos < pos_baseline && el.count_pos > this.min_channel_rate){
                pos_baseline = el.count_pos
            }
            if(el.count_neg < neg_baseline && el.count_neg > this.min_channel_rate){
                neg_baseline = el.count_neg
            }
        });
        return {pos:pos_baseline, neg:neg_baseline, pos_median:pos_median, neg_median:neg_median};
    }

    initPlot() {
        if (this.base === null)
            return;

        const width = this.base!.clientWidth - 20;  // subtract 20 to compensate for margins
        const height = this.base!.clientHeight - 20;

        this.spectrum_width = width -
            this.plot_margin.left - this.plot_margin.right;
        this.spectrum_height = height -
            this.plot_margin.top - this.plot_margin.bottom;

        d3.select(this.base!).select("svg").remove();

        const spectrum_svg = d3.select(this.base!).append("svg")
            .attr("width", `${width}px`)
            .attr("height", `${height}px`);

        const zoom = d3.zoom()
            .scaleExtent([1, 10])
            .translateExtent([[0, 0], [this.spectrum_width, this.spectrum_height]])
            .on("zoom", this.zoomed.bind(this));

        this.spectrum_g = spectrum_svg.append('g').attr(
            'transform', 'translate(' + this.plot_margin.left + ',' +
            this.plot_margin.top + ')');

        this.spectrum_g.selectAll('.plot-axis').remove();
        this.spectrum_g.selectAll('path').remove();

        this.spectrum2_g = spectrum_svg.append('g').attr(
            'transform', 'translate(' + this.plot_margin.left + ',' +
            this.plot_margin.top + ')');
        this.spectrum2_g.selectAll('path').remove();

        this.spectrum_x =
            d3.scaleLinear().rangeRound([0, this.spectrum_width]);

        if ((this.spectrum_width > 0) && (this.spectrum_height > 0))
            this.spectrum_g.append("rect")
                .attr("class", "spectrum-overlay")
                .attr("fill", "transparent")
                .attr("width", this.spectrum_width)
                .attr("height", this.spectrum_height)
                .call(zoom.bind(this))
                .on("dblclick.zoom", null)
                .on("click", this.mouseclick.bind(this));

            this.spectrum_g = spectrum_svg.append('g').attr(
                'transform', 'translate(' + this.plot_margin.left + ',' +
                this.plot_margin.top + ')');

    }

    renderPlot() {
        //Plot Scale
        if (this.state.spectrum_log_scale) {
            this.spectrum_y =
                d3.scaleLog().rangeRound([this.spectrum_height, 0]);
            this.spectrum2_y =
                d3.scaleLog().rangeRound([this.spectrum_height, 0]);
        } else {
            this.spectrum_y =
                d3.scaleLinear().rangeRound([this.spectrum_height, 0]);
            this.spectrum2_y =
                d3.scaleLinear().rangeRound([this.spectrum_height, 0]);
        }

        //Spectrum Plot
        const max_above_minval = 100;
        var source_spectrum = this.props.data;
        if (this.spectrum_x !== null && this.spectrum_y !== null && this.spectrum2_y !== null) {
            this.spectrum_x.domain(
                [d3.min(this.drift_time),
                d3.max(this.drift_time)]);


            /*
            this.spectrum_y.domain(
                [d3.min(source_spectrum, function (d) { return  d.count_pos}),
                d3.max(source_spectrum, function (d) { return d.count_pos})]
            );
            this.spectrum2_y.domain(
                [d3.min(source_spectrum, function (d) { return d.count_neg}),
                d3.max(source_spectrum, function (d) { return d.count_neg})]
            );
            */

            const median_scale = 3;
            const baseline = this.find_data_baseline();
            const spectrum_maxval_y = baseline.pos_median * median_scale < baseline.pos+max_above_minval ? baseline.pos+max_above_minval : baseline.pos_median*median_scale;
            const spectrum2_maxval_y = baseline.neg_median * median_scale < baseline.neg+max_above_minval ? baseline.neg+max_above_minval : baseline.neg_median*median_scale;;

            this.spectrum_y.domain(
                [baseline.pos,
                spectrum_maxval_y]
            );

            this.spectrum2_y.domain(
                [baseline.neg,
                spectrum2_maxval_y]
            );


            var xscale = this.spectrum_x;
            var yscale = this.spectrum_y;
            var yscale2 = this.spectrum2_y;

            if (this.state.spectrum_transform !== undefined) {
                this.check_bounds_spectral_transform(this.state.spectrum_transform);
                xscale = this.state.spectrum_transform.rescaleX(this.spectrum_x);
            }

            // clang-format off
            this.spectrum_g.selectAll('g').remove();
            this.spectrum_g.selectAll('path').remove();
            this.spectrum_g.selectAll(".focus-line").remove();
            this.spectrum_g.selectAll(".focus-line-fat").remove();
            this.spectrum_g.selectAll(".focus-line-text").remove();
            this.spectrum_g.selectAll(".strong-lines").remove();
            this.spectrum_g.selectAll(".focus").remove();
            this.spectrum2_g.selectAll('g').remove();
            this.spectrum2_g.selectAll('path').remove();

            this.spectrum_g.append('g')
                .attr('class', 'plot-axis')
                .attr('transform', 'translate(0,' + this.spectrum_height + ')')
                .call(d3.axisBottom(xscale).scale(xscale));

                this.spectrum_g.append('g')
                .attr('class', 'plot-axis')
                .attr('transform', 'translate('+(this.spectrum_width/2)+',' +(this.spectrum_height+this.plot_margin.bottom*0.91)+ ')')
                .append("text")
                .attr("text-anchor", "middle")  // this makes it easy to centre the text as the transform is applied to the anchor
                .attr("fill",'lightgrey')
                .style("font-size", "10px")
                .text(t("Drift Time [ms]",{ns:'trans'}));

            this.spectrum_g.append('g')
                .attr('class', 'plot-axis spectrum-axis')
                .call(d3.axisLeft(this.spectrum_y).ticks(5,d3.format(",d")));

            this.spectrum_g.append('path')
                .datum(source_spectrum)
                .attr('class', 'spectrum-area')
                .attr('fill', 'none')
                .attr('stroke', 'olivedrab')
                .attr('stroke-width', 1.5)
                .attr('d', d3.line()
                    .defined((d: any) => {
                        return d.drift_time < xscale.domain()[1] && d.drift_time > xscale.domain()[0] && d.count_pos > this.min_channel_rate;
                    })
                    .x((d: any) => { return xscale(d.drift_time) as number; })
                    .y((d: any) => { return yscale(d.count_pos) as number; }))

            this.spectrum2_g.append('path')
                .datum(source_spectrum)
                .attr('class', 'spectrum-area')
                .attr('fill', 'none')
                .attr('stroke', 'steelblue')
                .attr('stroke-width', 1.5)
                .attr('d', d3.line()
                    .defined((d: any) => {
                        return d.drift_time < xscale.domain()[1] && d.drift_time > xscale.domain()[0] && d.count_neg > this.min_channel_rate;
                    })
                    .x((d: any) => { return xscale(d.drift_time) as number; })
                    .y((d: any) => { return yscale2(d.count_neg) as number; }));


            this.spectrum_g.append('g')
                .attr('class', 'plot-axis')
                .append("text")
                .attr("text-anchor", "middle")  // this makes it easy to centre the text as the transform is applied to the anchor
                .attr("transform", "translate(-" + this.plot_margin.left * 0.7 + "," + (this.spectrum_height / 2) + ")rotate(-90)")  // text is drawn off the screen top left, move down and out and rotate
                .attr("fill", 'olivedrab')
                .text(t("Pos. Mode Signal Strength",{ns:'trans'}));

            this.spectrum2_g.append('g')
                .attr('class', 'plot2-axis')
                .attr('transform', 'translate('+ this.spectrum_width + ',0)')
                .call(d3.axisRight(this.spectrum2_y).ticks(5,d3.format(",d")));

            this.spectrum2_g.append('g')
                .attr('class', 'plot2-axis')
                .append("text")
                .attr("text-anchor", "middle")  // this makes it easy to centre the text as the transform is applied to the anchor
                .attr("transform", "translate("+ (this.spectrum_width+this.plot_margin.right*0.70) +","+(this.spectrum_height/2)+")rotate(90)")  // text is drawn off the screen top left, move down and out and rotate
                .attr("fill",'steelblue')
                .text(t("Neg. Mode Signal Strength",{ns:'trans'}));

            if (this.state.show_spectrum_cursor) {
                this.renderCursor();
            }

            //console.log(this.state.spectrum_transform)
        }
    }

    renderCursor() {
        if (this.spectrum_x !== null && this.spectrum_y !== null) {
            var d = this.calculate_cursor_location();
            if (this.state.focus_location_i !== undefined) {

                this.spectrum_g.selectAll(".focus-line").remove();
                this.spectrum_g.selectAll(".focus-line-fat").remove();
                this.spectrum_g.selectAll(".focus-line-text").remove();
                //this.spectrum_g.selectAll(".strong-lines").remove();
                this.spectrum_g.selectAll(".focus").remove();
                this.spectrum_g.selectAll(".focus2").remove();

                var xscale = this.spectrum_x;
                if (this.state.spectrum_transform !== undefined) {
                    xscale = this.state.spectrum_transform.rescaleX(this.spectrum_x);
                }

                if (!isNaN(d.count_pos)) {
                    this.focus = this.spectrum_g.append("g")
                        .attr("class", "focus")
                        .attr("transform", "translate(" + xscale(d.drift_time) + "," + this.spectrum_y(d.count_pos) + ")")
                        .style("display", null);
                    this.focus.append("circle")
                        .attr("r", 4.5);

                    this.focus2 = this.spectrum_g.append("g")
                        .attr("class", "focus")
                        .attr("transform", "translate(" + xscale(d.drift_time) + "," + this.spectrum_y(d.count_neg) + ")")
                        .style("display", null);
                    this.focus2.append("circle")
                        .attr("r", 4.5);


                    if (this.state.show_spectrum_cursor) {
                        if (d.drift_time < xscale.domain()[1] && d.drift_time > xscale.domain()[0]) {
                            this.focus.style("display", null);
                            this.focus2.style("display", null);
                        } else {
                            this.focus.style("display", "none");
                            this.focus2.style("display", "none");
                        }
                    } else {
                        this.focus.style("display", "none");
                        this.focus2.style("display", "none");
                    }

                    let dragCursor = d3.drag()
                        .on("start", ()=>{this.cursor_grab(true)})
                        .on("end", ()=>{this.cursor_grab(false)})
                        .on("drag", this.cursor_dragged.bind(this));
                    this.focus.attr("transform", "translate(" + xscale(d.drift_time) + "," + this.spectrum_y(d.count_pos) + ")");
                    this.focus2.attr("transform", "translate(" + xscale(d.drift_time) + "," + this.spectrum_y(d.count_neg) + ")");
                    let str = d.drift_time.toFixed(1) + "ms : " + d.count_pos.toFixed(1) + " / "+ d.count_neg.toFixed(1)

                    if (this.focus.style("display") !== "none") {
                        this.spectrum_g.append("line")
                            .attr('class', 'focus-line')
                            .attr("x1", xscale(d.drift_time))
                            .attr("y1", 0)
                            .attr("x2", xscale(d.drift_time))
                            .attr("y2", this.spectrum_height)
                            .style("stroke-width", 1)
                            .style("stroke", "steelblue")
                            .style("fill", "none");
                        let grab_cursor = "grab";
                        if(this.cursor_mousedown_flag){
                            grab_cursor = "grabbing";
                        }
                        this.spectrum_g.append("line")
                            .attr('class', 'focus-line-fat')
                            .attr("x1", xscale(d.drift_time))
                            .attr("y1", 0)
                            .attr("x2", xscale(d.drift_time))
                            .attr("y2", this.spectrum_height)
                            .style("stroke", "transparent")
                            .attr('stroke-width', 20)
                            .style("fill", "none")
                            .style("cursor",grab_cursor)
                            .call(dragCursor.bind(this));
                        this.spectrum_g.append("text")
                            .text(str)
                            .attr('class', 'focus-line-text')
                            .attr("x", xscale(d.drift_time))
                            .attr("y", 0)
                            .attr("dy", ".35em")
                            .attr("cursor","pointer")
                            .on("click", this.toggle_spectrum_cursor.bind(this));
                    }
                }
            }
        }

    }

    render() {
        console.log('Render ImsSpectrumPlot @'+Date.now())
        let linearSelectClass;
        let logSelectClass;
        if (this.state.spectrum_log_scale) {
            linearSelectClass = "";
            logSelectClass = "active";
        } else {
            linearSelectClass = "active";
            logSelectClass = "";
        }
        return (
            <div className="Plot" style={{ display: 'flex', flex: '1', flexDirection: 'column' }}>
                <div className="header">
                    <span>{t(this.props.title,{ns:'trans'})}</span>
                    <div className="icon-bar" style={{ paddingLeft: '10px', paddingBottom: '2px' }}>
                        <div className={"axis-type " + linearSelectClass} onClick={() => this.set_spectrum_log_scale(false)}>{t('LINEAR',{ns:'trans'})}</div>
                        <div className={"axis-type " + logSelectClass} onClick={() => this.set_spectrum_log_scale(true)}>{t('LOG',{ns:'trans'})}</div>
                    </div>
                </div>
                <div className="plot" ref={this.updateRef} style={{ display: 'flex', flex: '1', touchAction:'none' }} />
            </div>
        )
    }
}
const MemoizedImsSpectrumPlot = React.memo(ImsSpectrumPlot,spectrumPlotPropsAreEqual)

function spectrumPlotPropsAreEqual(prevProps:ImsSpectrumPlotProps,nextProps:ImsSpectrumPlotProps){
    const sumPosPrevProps = d3.sum(prevProps.data, function (d) { return d.count_pos});
    const sumNegPrevProps = d3.sum(prevProps.data, function (d) { return d.count_neg });
    const sumPosNextProps = d3.sum(nextProps.data, function (d) { return d.count_pos});
    const sumNegNextProps = d3.sum(nextProps.data, function (d) { return d.count_neg });
    let flag = sumPosPrevProps === sumPosNextProps && sumNegPrevProps === sumNegNextProps &&prevProps.dominantIsotope === nextProps.dominantIsotope;
    return (flag);
}
